<template>
  <div class="auth-page">
    <div class="auth-card">
      <loader :loading="loading"></loader>

      <!-- Header -->
      <div class="auth-header">
        <div class="auth-icon-wrap">
          <v-icon color="white" size="28">mdi-lock-reset</v-icon>
        </div>
        <h1 class="auth-title">{{ $t('password.reset') }}</h1>
        <p class="auth-subtitle">Enter your username and we'll send you a reset link</p>
      </div>

      <v-alert type="error" v-if="errorSummary !== null" class="auth-alert" dense rounded="lg">
        {{ errorSummary }}
      </v-alert>

      <!-- Form -->
      <v-form ref="form" :lazy-validation="true" class="auth-form">
        <v-text-field
          :label="$t('username')"
          v-model="username"
          :placeholder="$t('username')"
          type="text"
          :rules="requiredRule()"
          outlined
          dense
          rounded
          autofocus
          prepend-inner-icon="mdi-account-outline"
          class="auth-input"
        ></v-text-field>
      </v-form>

      <!-- Actions -->
      <div class="auth-actions">
        <v-btn to="/" class="auth-btn-back" rounded outlined>
          <v-icon left size="18">mdi-arrow-left</v-icon>
          {{ $t('back') }}
        </v-btn>
        <v-btn class="auth-btn-submit" rounded dark @click="submit" :disabled="loading">
          <v-icon left size="18">mdi-email-fast-outline</v-icon>
          {{ $t('reset') }}
        </v-btn>
      </div>

      <!-- Links -->
      <div class="auth-links">
        <p class="auth-link-text">
          {{ $t('no-account') }}
          <router-link :to="{ name: 'SignUp' }" class="auth-link auth-link--accent">
            {{ $t('sign-up') }}
          </router-link>
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import util from '@/mixins/util';
import EventBus from '@/helpers/event-bus';
import Loader from '@/components/helpers/Loader';

export default {
  name: 'ForgotPassword',
  mixins: [util],
  components: {
    Loader,
  },
  data: () => ({
    loading: false,
    username: null,
    rememberMe: true,
    responseStatus: null,
    errorSummary: null,
  }),
  mounted() {
    this.clearLocalStorage();
  },
  methods: {
    submit() {
      this.$refs.form.validate();
      if (!this.username) {
        return;
      }
      this.loading = true;
      this.axios
        .post('/api/v1/auth/forgot-password', this.username)
        .then(() => {
          console.log('Success');
          EventBus.$emit('snackbar', {
            text: (this.errorSummary = this.$i18n.t('errorlist.reset-link')),
            color: 'success',
          });
          this.$router.push({
            name: 'LogIn',
          });
        })
        .catch(error => {
          console.log(error.response);
          if (error.request && error.response.status === 400) {
            this.errorSummary = this.$i18n.t('errorlist.error-400');
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
};
</script>

<style lang="scss" scoped>
.auth-page {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: calc(100vh - 160px);
  padding: 40px 16px;
  background: linear-gradient(160deg, #f0f4f8 0%, #e8edf5 50%, #f5f1f8 100%);
}

.auth-card {
  width: 100%;
  max-width: 440px;
  background: #ffffff;
  border-radius: 20px;
  padding: 40px 36px 32px;
  box-shadow: 0 8px 40px rgba(26, 35, 126, 0.08), 0 2px 12px rgba(0, 0, 0, 0.04);
  border: 1px solid rgba(0, 0, 0, 0.04);
}

.auth-header {
  text-align: center;
  margin-bottom: 28px;
}

.auth-icon-wrap {
  width: 56px;
  height: 56px;
  border-radius: 16px;
  background: linear-gradient(135deg, #00838f 0%, #00acc1 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px;
  box-shadow: 0 4px 16px rgba(0, 131, 143, 0.25);
}

.auth-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1a237e;
  margin-bottom: 4px;
}

.auth-subtitle {
  font-size: 0.92rem;
  color: #757575;
  margin: 0;
}

.auth-alert {
  margin-bottom: 20px;
}

.auth-form {
  margin-bottom: 8px;
}

.auth-input {
  margin-bottom: 4px;
}

.auth-actions {
  display: flex;
  gap: 12px;
  margin-bottom: 24px;
}

.auth-btn-back {
  flex: 1;
  text-transform: none !important;
  font-weight: 600 !important;
  letter-spacing: 0.3px !important;
  border-color: #bdbdbd !important;
  color: #616161 !important;
  transition: all 0.3s ease !important;
}

.auth-btn-back:hover {
  border-color: #1a237e !important;
  color: #1a237e !important;
  background: rgba(26, 35, 126, 0.04) !important;
}

.auth-btn-submit {
  flex: 1;
  background: linear-gradient(135deg, #00838f 0%, #00acc1 100%) !important;
  text-transform: none !important;
  font-weight: 700 !important;
  letter-spacing: 0.3px !important;
  box-shadow: 0 4px 16px rgba(0, 131, 143, 0.25) !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

.auth-btn-submit:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 24px rgba(0, 131, 143, 0.35) !important;
}

.auth-links {
  text-align: center;
}

.auth-link {
  font-size: 0.88rem;
  color: #1976d2;
  text-decoration: none;
  transition: color 0.2s ease;
}

.auth-link:hover {
  color: #1a237e;
  text-decoration: underline;
}

.auth-link--accent {
  font-weight: 600;
  color: #00838f;
}

.auth-link--accent:hover {
  color: #00acc1;
}

.auth-link-text {
  font-size: 0.88rem;
  color: #9e9e9e;
  margin-top: 8px;
  margin-bottom: 0;
}
</style>
